import React, { useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Button,
  MenuItem,
  FormControl,
  Select,
  Box,
  Icon,
  Menu,
  CircularProgress,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { fieldStyles } from "services/utils";
import MDButton from "components/MDButton";
import { revokeAllAccess } from "services/api";
import { grantAllAccess } from "services/api";
import { grantAccess } from "services/api";
import { revokeAccess } from "services/api";
import SnackbarWrapper from "components/Snackbar";
import { getEmployeeModules } from "services/api";
import { useAuth } from "context/authContext";

const moduleIcons = {
  "Strategic Info": "star",
  Policies: "gavel",
  Organization: "apartment",
  Performance: "leaderboard",
  Competencies: "school",
  Trainings: "workspace_premium",
  "Succession Plan": "emoji_events",
  "Talent Acquisition": "person_search",
};

const modules = [
  "Strategic Info",
  "Organization",
  "Policies",
  "Performance",
  "Competencies",
  "Trainings",
  "Succession Plan",
  "Talent Acquisition",
];

const AccessRightsDialog = ({ open, onClose, employeeId, employeeName }) => {
  const { t } = useTranslation();
  const snackbarRef = useRef();
  const { employee, companyModules } = useAuth();
  const [accessRights, setAccessRights] = useState({});
  const [anchorEl, setAnchorEl] = useState(null);
  const [loading, setLoading] = useState(false);

  const availableModules = useMemo(() => {
    // Define modules that must always appear
    const mandatoryModules = ["Strategic Info", "Organization"];

    // Safety check: if data hasn't loaded yet, just return the mandatory ones
    if (!companyModules || !Array.isArray(companyModules)) {
      return mandatoryModules;
    }

    // Check if there is an active "ALL" module
    const hasAllAccess = companyModules.some((cm) => cm.moduleName === "ALL" && cm.active === true);

    if (hasAllAccess) {
      return modules; // Return all static modules
    }

    // Filter the static list
    return modules.filter((staticModuleName) => {
      // 1. Always keep mandatory modules
      if (mandatoryModules.includes(staticModuleName)) {
        return true;
      }

      // 2. For others, check if they exist in companyModules AND are active
      const foundModule = companyModules.find((cm) => cm.moduleName === staticModuleName);

      return foundModule && foundModule.active === true;
    });
  }, [companyModules]);

  useEffect(() => {
    const fetchModules = async () => {
      setLoading(true);
      if (employeeId) {
        const moduless = await getEmployeeModules(employeeId);

        if (moduless !== "error") {
          const mappedAccess = moduless.reduce((acc, curr) => {
            // 3. Only add to state if the module is in our availableModules list
            if (availableModules.includes(curr.moduleName)) {
              acc[curr.moduleName] = curr.accessLevel;
            }
            return acc;
          }, {});

          setAccessRights(mappedAccess);
        } else {
          snackbarRef.current?.showSnackbar(t("messages.loadModules.error"), "error");
        }
      }
      setLoading(false);
    };

    // Recalculate if employeeId changes or if the calculated allowed modules change
    fetchModules();
  }, [employeeId, availableModules, t]);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = (value) => {
    setAnchorEl(null);
    if (value) {
      onGrantAll(value); // 🔹 Pass selected value back
    }
  };

  const accessOptions = [
    { value: "none", label: t("admin.employees.accessRights.none") },
    { value: "view", label: t("admin.employees.accessRights.view") },
    { value: "edit", label: t("admin.employees.accessRights.edit") },
  ];

  const onRevokeAll = async () => {
    if (employeeId === employee.id) {
      snackbarRef.current?.showSnackbar(
        t("admin.employees.accessRights.messages.revokeAll.error"),
        "error"
      );
      return;
    }
    const res = await revokeAllAccess(employeeId);
    if (res !== "error") {
      setAccessRights({});
      snackbarRef.current?.showSnackbar(
        t("admin.employees.accessRights.messages.revokeAll.success"),
        "success"
      );
    } else {
      snackbarRef.current?.showSnackbar(
        t("admin.employees.accessRights.messages.revokeAll.error"),
        "error"
      );
    }
  };

  const onChangeAccess = async (module, right) => {
    if (employeeId === employee.id && module === "Organization") {
      snackbarRef.current?.showSnackbar(
        t("admin.employees.accessRights.messages.changeAccess.error", { module }),
        "error"
      );
      return;
    }
    if (right !== "none") {
      const res = await grantAccess(employeeId, module, right);
      if (res !== "error") {
        setAccessRights((prev) => ({ ...prev, [module]: right }));
        snackbarRef.current?.showSnackbar(
          t("admin.employees.accessRights.messages.changeAccess.grantSuccess", { module, right }),
          "success"
        );
      } else {
        snackbarRef.current?.showSnackbar(
          t("admin.employees.accessRights.messages.changeAccess.error", { module }),
          "error"
        );
      }
    } else {
      const res = await revokeAccess(employeeId, module);
      if (res !== "error") {
        setAccessRights((prev) => ({ ...prev, [module]: right }));
        snackbarRef.current?.showSnackbar(
          t("admin.employees.accessRights.messages.changeAccess.revokeSuccess", { module }),
          "success"
        );
      } else {
        snackbarRef.current?.showSnackbar(
          t("admin.employees.accessRights.messages.changeAccess.error", { module }),
          "error"
        );
      }
    }
  };

  const onGrantAll = async (value) => {
    const res = await grantAllAccess(employeeId, value);
    if (res !== "error") {
      setAccessRights({
        "Strategic Info": value,
        Policies: value,
        Organization: value,
        Performance: value,
        Competencies: value,
        Trainings: value,
        "Succession Plan": value,
        "Talent Acquisition": value,
      });
      snackbarRef.current?.showSnackbar(
        t("admin.employees.accessRights.messages.grantAll.success", { value }),
        "success"
      );
    } else {
      snackbarRef.current?.showSnackbar(
        t("admin.employees.accessRights.messages.grantAll.error"),
        "error"
      );
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>
        {t("admin.employees.employeeDialog.fields.accessRights")} {employeeName}
      </DialogTitle>
      {loading ? (
        <Box display="flex" justifyContent="center" p={1}>
          <CircularProgress size={20} sx={{ color: "black" }} />
        </Box>
      ) : (
        <DialogContent dividers>
          {/* 🔹 Revoke All Button */}
          <Box display="flex" justifyContent="flex-end" gap={2} mb={2}>
            <MDButton variant="outlined" color="success" onClick={handleClick}>
              {t("admin.employees.accessRights.grantAll")}
            </MDButton>
            <MDButton variant="outlined" color="error" onClick={onRevokeAll}>
              {t("admin.employees.accessRights.revokeAll")}
            </MDButton>
            <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => handleClose(null)}>
              <MenuItem onClick={() => handleClose("view")}>
                {t("admin.employees.accessRights.view")}
              </MenuItem>
              <MenuItem onClick={() => handleClose("edit")}>
                {t("admin.employees.accessRights.edit")}
              </MenuItem>
            </Menu>
          </Box>

          {/* 🔹 Modules List */}
          {availableModules.map((module) => (
            <Box
              key={module}
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              mb={2}
            >
              {/* Module name + icon */}
              <Box display="flex" alignItems="center" gap={1}>
                <Icon fontSize="small">{moduleIcons[module]}</Icon>
                <Typography variant="body1">
                  {/* {module} */}
                  {t(`admin.modules.${module}`)}
                </Typography>
              </Box>

              {/* Dropdown */}
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <Select
                  value={accessRights[module] || "none"}
                  onChange={(e) => onChangeAccess(module, e.target.value)}
                  sx={fieldStyles}
                >
                  {accessOptions.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          ))}
        </DialogContent>
      )}
      <DialogActions>
        <Button onClick={onClose}>{t("common.close")}</Button>
      </DialogActions>
      <SnackbarWrapper
        ref={snackbarRef}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      />
    </Dialog>
  );
};

AccessRightsDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  employeeId: PropTypes.string.isRequired,
  employeeName: PropTypes.string.isRequired,
};

export default AccessRightsDialog;
